import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-12985">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-12986">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-12987">
              <span className="text-xs font-bold text-indigo-800" data-id="element-12988">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-12989">
            <div className="text-center" data-id="element-12990">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-12991">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-12992">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-12993">→</div>
            <div className="text-center" data-id="element-12994">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-12995">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-12996">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-12997">
            <table className="w-full text-sm" data-id="element-12998">
              <thead data-id="element-12999">
                <tr className="bg-indigo-100/70" data-id="element-13000">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13001">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13002">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-13003">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-13004">
                      <td className="px-3 py-1.5" data-id="element-13005">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-13006">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-13007">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13008">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-13009">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-13010">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-13011">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-13012">
            <span className="text-2xl" data-id="element-13013">📝</span>
            <div className="text-left" data-id="element-13014">
              <div className="text-sm font-bold text-indigo-900" data-id="element-13015">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-13016">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-13017">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-13018">
            <div className="text-center" data-id="element-13019">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13020">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-13021">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-13022">→</div>
            <div className="text-center" data-id="element-13023">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13024">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-13025">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-13026">
          <table className="w-full" data-id="element-13027">
            <thead data-id="element-13028">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-13029">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13030">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13031">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-13032">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-13033">
                    <td className="px-4 py-3" data-id="element-13034">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-13035">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-13036">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13037">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-13038">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-13039">
            <div className="flex items-start gap-3" data-id="element-13040">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-13041">
                <span className="text-blue-600 text-lg" data-id="element-13042">🔍</span>
              </div>
              <div data-id="element-13043">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-13044">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13045">
                  De <strong data-id="element-13046">stam</strong> krijg je door <strong data-id="element-13047">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-13048">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-13049">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13050">
              <div className="flex items-start gap-3" data-id="element-13051">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13052">
                  <span className="text-emerald-600 text-lg" data-id="element-13053">📋</span>
                </div>
                <div data-id="element-13054">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13055">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13056">
                    <p data-id="element-13057">
                      <strong data-id="element-13058">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13059">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-13060">
                      <strong data-id="element-13061">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13062">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13063">
                      <strong data-id="element-13064">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13065">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13066">
                      <strong data-id="element-13067">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13068">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13069">
              <div className="flex items-start gap-3" data-id="element-13070">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13071">
                  <span className="text-emerald-600 text-lg" data-id="element-13072">📋</span>
                </div>
                <div data-id="element-13073">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13074">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13075">
                    <p data-id="element-13076">
                      <strong data-id="element-13077">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-13078">
                      <strong data-id="element-13079">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13080">
                      <em data-id="element-13081">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13082">
              <div className="flex items-start gap-3" data-id="element-13083">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13084">
                  <span className="text-emerald-600 text-lg" data-id="element-13085">📋</span>
                </div>
                <div data-id="element-13086">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13087">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13088">
                    <p data-id="element-13089">
                      <strong data-id="element-13090">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-13091">
                      <strong data-id="element-13092">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13093">
                      <em data-id="element-13094">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-13095">
            <div className="flex items-start gap-3" data-id="element-13096">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-13097">
                <span className="text-amber-600 text-lg" data-id="element-13098">💡</span>
              </div>
              <div data-id="element-13099">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-13100">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13101">
                  Bij <strong data-id="element-13102">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-13103">jij loopt</em> wordt{' '}
                  <em data-id="element-13104">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}